import dayjs from "dayjs";
import { SITE_CONFIG } from "@/constants/siteConfig";
import { formatCityName } from "@/lib/formatCityName";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const parsed = dayjs(dateStr);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(dateStr);
};

const HTML_ENTITIES = {
  nbsp: " ",
  amp: "&",
  quot: '"',
  apos: "'",
  lt: "<",
  gt: ">",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  rsquo: "’",
  lsquo: "‘",
  rdquo: "”",
  ldquo: "“",
  bull: "•",
  rupee: "₹",
};

// Event descriptions come from a rich-text editor as entity-encoded HTML.
// Stripping only the tags shipped meta descriptions like
// "Mr.&nbsp;&amp;&nbsp;Miss&nbsp;Gujarat" — one unbroken "word", which the
// length cut then reduced to "Mr.&nbsp…". Entities are decoded twice because
// the editor double-encodes some ("&amp;nbsp;").
const decodeEntities = (text) => {
  let out = text;
  for (let pass = 0; pass < 2; pass++) {
    out = out.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code) => {
      if (code[0] === "#") {
        const n =
          code[1].toLowerCase() === "x"
            ? parseInt(code.slice(2), 16)
            : parseInt(code.slice(1), 10);
        return Number.isFinite(n) && n > 0 ? String.fromCodePoint(n) : match;
      }
      return HTML_ENTITIES[code.toLowerCase()] ?? match;
    });
  }
  return out;
};

const stripHtml = (html) =>
  typeof html === "string"
    ? decodeEntities(html.replace(/<[^>]*>/g, " "))
        .replace(/<\/?[a-z][^>]*>/gi, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";

const toMetaDescription = (text, maxLength = 160) => {
  const plain = stripHtml(text);
  return plain.length > maxLength
    ? plain.slice(0, maxLength).replace(/\s+\S*$/, "…")
    : plain;
};

const extractFirstImageUrl = (img) => {
  if (!img) return "";
  if (typeof img === "string") return img;
  if (Array.isArray(img)) return extractFirstImageUrl(img[0]);
  if (typeof img === "object") return img.url || img.thumbnail || img.src || "";
  return "";
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  if (typeof timeStr === "string" && /[AaPp][Mm]/.test(timeStr)) {
    return timeStr.trim();
  }
  const parsed = dayjs(`2000-01-01 ${timeStr}`);
  return parsed.isValid() ? parsed.format("hh:mm A") : String(timeStr);
};

export const buildDynamicContext = ({
  city,
  state,
  country,
  pincode,
  event,
  blog,
  artist,
  venue,
  organizer,
  category,
  contest,
  user,
  booking,
  cmsEntry,
  custom,
} = {}) => {
  const now = dayjs();

  // Auto-extract from cmsEntry if specific entities weren't passed directly
  let resolvedBlog = blog;
  let resolvedArtist = artist;
  let resolvedEvent = event;
  let resolvedCategory = category;

  if (cmsEntry) {
    if (!resolvedBlog && (cmsEntry.group === "blog_page" || cmsEntry.entity_type === "blog")) {
      const blogSection = cmsEntry.content?.sections?.find(
        (s) => (s.type ?? s.component) === "BlogDetail",
      );
      resolvedBlog = blogSection?.data ?? cmsEntry.entity_data ?? cmsEntry;
    }

    if (!resolvedArtist && (cmsEntry.group === "artist_page" || cmsEntry.entity_type === "artist")) {
      resolvedArtist = cmsEntry.entity_data ?? cmsEntry;
    }

    if (!resolvedEvent && (cmsEntry.group === "event_page" || cmsEntry.entity_type === "event")) {
      resolvedEvent = cmsEntry.entity_data ?? cmsEntry;
    }

    if (!resolvedCategory && (cmsEntry.group === "category" || cmsEntry.entity_type === "category")) {
      // City-category rows carry the real category as `cmsEntry.category`
      // ({ name: "Comedy Shows" }). Falling back to the row itself made
      // {{category_name}} its page title, "Comedy Shows in Ahmedabad", and
      // templates like "{{category_name}} in {{city_name}}" doubled the city.
      resolvedCategory =
        (cmsEntry.category?.name ? cmsEntry.category : null) ??
        cmsEntry.entity_data ??
        cmsEntry;
    }

    if (!contest && (cmsEntry.group === "contest" || cmsEntry.entity_type === "contest")) {
      contest = cmsEntry.entity_data ?? cmsEntry;
    }
  }

  // Venue extraction
  const venueObj =
    typeof venue === "object" && venue !== null
      ? venue
      : typeof resolvedEvent?.venue === "object" && resolvedEvent?.venue !== null
        ? resolvedEvent.venue
        : {};
  const venueStr =
    typeof venue === "string"
      ? venue
      : typeof resolvedEvent?.venue === "string"
        ? resolvedEvent.venue
        : "";
  // get-venue-details carries the venue's name only on its address, which
  // left venue pages titled "Events at , Ahmedabad".
  const venueName =
    venueObj?.name ||
    venueObj?.title ||
    venueObj?.address?.name ||
    venueObj?.address?.line1 ||
    venueStr ||
    "";
  const venueAddrObj =
    typeof venueObj?.address === "object" && venueObj?.address !== null
      ? venueObj.address
      : null;

  const venueAddress =
    venueStr ||
    (typeof venueObj?.address === "string"
      ? venueObj.address
      : [
          venueObj?.line1 || venueAddrObj?.line1,
          venueObj?.line2 || venueAddrObj?.line2,
          venueObj?.city || venueAddrObj?.city,
          venueObj?.state || venueAddrObj?.state,
          venueObj?.country || venueAddrObj?.country,
        ]
          .filter(Boolean)
          // line1/line2 often already contain the city, state and country
          // (a venue that's yet to be announced stores the city itself as
          // line1 "Ahmedabad", line2 "Gujarat, India"), which read as
          // "Ahmedabad, Gujarat, India, Ahmedabad, Gujarat, India" in FAQ
          // answers. Keep each comma-separated part once.
          .flatMap((part) => String(part).split(","))
          .map((part) => part.trim())
          .filter(
            (part, i, parts) =>
              part &&
              parts.findIndex((p) => p.toLowerCase() === part.toLowerCase()) ===
                i,
          )
          .join(", "));

  const effectiveCity =
    venueObj?.city || formatCityName(city || "") || "";

  // Blog extraction
  const blogObj = resolvedBlog || {};
  const blogTitle = blogObj.title || blogObj.name || "";
  const blogSlug = (blogObj.slug || "").replace(/^blogs\//, "");
  const rawAuthor =
    blogObj.authorName ||
    blogObj.author?.name ||
    (typeof blogObj.author === "string" ? blogObj.author : null) ||
    blogObj.written_by?.name ||
    (typeof blogObj.written_by === "string" ? blogObj.written_by : null) ||
    blogObj.created_by?.name ||
    (typeof blogObj.created_by === "string" ? blogObj.created_by : null) ||
    "";
  const blogAuthor =
    typeof rawAuthor === "string"
      ? rawAuthor
      : typeof rawAuthor === "object" && rawAuthor !== null
        ? rawAuthor.name || rawAuthor.title || ""
        : String(rawAuthor || "");
  const blogDate = formatDate(
    blogObj.date ||
      blogObj.published_date ||
      blogObj.created_at ||
      blogObj.article_published_time,
  );
  const blogReadingTime = blogObj.reading_time
    ? String(blogObj.reading_time)
    : "";
  const rawTags = blogObj.tag || blogObj.tags || [];
  const blogTagsArray = Array.isArray(rawTags)
    ? rawTags
        .map((t) =>
          typeof t === "object" && t !== null
            ? t.name || t.tag || t.title
            : String(t),
        )
        .filter(Boolean)
    : typeof rawTags === "string"
      ? [rawTags]
      : [];
  const blogCategory = blogTagsArray[0] || "";
  const blogTags = blogTagsArray.join(", ");
  const blogExcerpt =
    blogObj.excerpt || blogObj.short_description || blogObj.sub_heading || "";
  const blogDescription = blogObj.description || "";
  const blogUrl = blogObj.slug
    ? `${SITE_CONFIG.platform_url || ""}/${blogObj.slug.startsWith("blogs/") ? blogObj.slug : `blogs/${blogObj.slug}`}`
    : "";

  // Artist extraction
  const artistObj = resolvedArtist?.artist || resolvedArtist || {};
  const artistFirstName = artistObj.first_name || "";
  const artistLastName = artistObj.last_name || "";
  const artistFullName =
    `${artistFirstName} ${artistLastName}`.trim() ||
    artistObj.name ||
    artistObj.title ||
    "";
  const artistAlternateName =
    artistObj.alternate_name || artistObj.stage_name || "";
  const artistSlug = (artistObj.slug || "").replace(/^artist\//, "");
  const artistType = artistObj.is_group ? "Music Group" : "Solo Artist";

  const rawGenres = artistObj.genres || [];
  const artistGenresArray = Array.isArray(rawGenres)
    ? rawGenres
        .map((g) =>
          typeof g === "object" && g !== null
            ? g.genre || g.name || g.title
            : String(g),
        )
        .filter(Boolean)
    : typeof rawGenres === "string"
      ? rawGenres
          .split(/[,&]+/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const artistGenre = artistGenresArray[0] || "";
  const artistGenres = artistGenresArray.join(", ");

  const rawArtistTags = artistObj.tags || [];
  const artistTagsArray = Array.isArray(rawArtistTags)
    ? rawArtistTags
        .map((t) =>
          typeof t === "object" && t !== null ? t.tag || t.name : String(t),
        )
        .filter(Boolean)
    : [];
  const artistTags = artistTagsArray.join(", ");

  const rawBadges = artistObj.badges || [];
  const artistBadgesArray = Array.isArray(rawBadges)
    ? rawBadges
        .map((b) =>
          typeof b === "object" && b !== null ? b.badge || b.name : String(b),
        )
        .filter(Boolean)
    : [];
  const artistBadges = artistBadgesArray.join(", ");

  const artistBio = artistObj.description || artistObj.bio || "";
  const artistEventsCount =
    artistObj.completed_events_count !== undefined &&
    artistObj.completed_events_count !== null
      ? String(artistObj.completed_events_count)
      : resolvedArtist?.completed_events?.length !== undefined
        ? String(resolvedArtist.completed_events.length)
        : artistObj.events_count !== undefined && artistObj.events_count !== null
          ? String(artistObj.events_count)
          : "";
  const artistUrl = artistObj.slug
    ? `${SITE_CONFIG.platform_url || ""}/${artistObj.slug.startsWith("artist/") ? artistObj.slug : `artist/${artistObj.slug}`}`
    : "";

  // Organizer extraction
  const organizerObj =
    typeof organizer === "object" && organizer !== null
      ? organizer
      : typeof resolvedEvent?.organizer === "object" && resolvedEvent?.organizer !== null
        ? resolvedEvent.organizer
        : {};
  const organizerName =
    organizerObj?.name ||
    organizerObj?.company_name ||
    (typeof organizer === "string" ? organizer : "") ||
    (typeof resolvedEvent?.organizer === "string" ? resolvedEvent.organizer : "") ||
    "";
  const organizerEmail =
    organizerObj?.email ||
    organizerObj?.contact_email ||
    resolvedEvent?.organizer_email ||
    "";
  const organizerPhone =
    organizerObj?.phone ||
    organizerObj?.contact_phone ||
    resolvedEvent?.organizer_phone ||
    "";
  const organizerWebsite =
    organizerObj?.website || resolvedEvent?.organizer_website || "";
  const organizerSlug = (
    organizerObj?.slug ||
    organizerObj?.workspace_slug ||
    resolvedEvent?.organizer_slug ||
    ""
  ).replace(/^organizer\//, "");
  const organizerWorkspaceSlug =
    organizerObj?.workspace_slug ||
    organizerSlug ||
    "";
  const organizerUrl = organizerSlug
    ? `${SITE_CONFIG.platform_url || ""}/organizer/${organizerSlug}`
    : "";
  const organizerDescription =
    organizerObj?.description ||
    organizerObj?.bio ||
    organizerObj?.about ||
    "";
  const organizerPublicEventsCount =
    organizerObj?.public_events_count !== undefined &&
    organizerObj?.public_events_count !== null
      ? String(organizerObj.public_events_count)
      : resolvedEvent?.organizer_events_count !== undefined &&
          resolvedEvent?.organizer_events_count !== null
        ? String(resolvedEvent.organizer_events_count)
        : "";
  const organizerAddrObj =
    typeof organizerObj?.address === "object" && organizerObj.address !== null
      ? organizerObj.address
      : null;
  const organizerCity =
    organizerAddrObj?.city || organizerObj?.city || "";
  const organizerState =
    organizerAddrObj?.state || organizerObj?.state || "";
  const organizerCountry =
    organizerAddrObj?.country || organizerObj?.country || "India";
  const organizerPincode =
    organizerAddrObj?.pincode || organizerObj?.pincode || "";
  const organizerAddress = [
    organizerAddrObj?.line1 || organizerObj?.line1,
    organizerAddrObj?.line2 || organizerObj?.line2,
    organizerCity,
    organizerState,
    organizerCountry,
  ]
    .filter(Boolean)
    .join(", ");
  const organizerLogo = extractFirstImageUrl(
    organizerObj?.logo ||
    organizerObj?.profile_pic ||
    organizerObj?.image,
  );

  // Category extraction
  const categoryObj = resolvedCategory || {};
  const rawCategoryName = categoryObj.name || categoryObj.title || "";
  // Some city-category rows (e.g. navratri-2026) have no category object, so
  // the row itself stands in and its title already names the city: "Navratri
  // 2026 in Ahmedabad". Strip that suffix so the name is just the category.
  const categoryName =
    cmsEntry && categoryObj === cmsEntry && effectiveCity
      ? rawCategoryName
        .replace(
          new RegExp(
            `\\s+in\\s+${effectiveCity.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*$`,
            "i",
          ),
          "",
        )
        .trim() || rawCategoryName
      : rawCategoryName;
  const categorySlug = (categoryObj.slug || "").replace(/^category\//, "");
  const categoryDescription = categoryObj.description || "";
  const categoryUrl = categorySlug
    ? `${SITE_CONFIG.platform_url || ""}/category/${categorySlug}`
    : "";

  // Contest extraction
  const contestObj = contest || {};
  const contestName = contestObj.name || contestObj.title || "";
  const contestSlug = (contestObj.slug || "").replace(/^contest\//, "");
  const contestStartDate = formatDate(
    contestObj.start_date || contestObj.startDate || contestObj.start_at,
  );
  const contestEndDate = formatDate(
    contestObj.end_date || contestObj.endDate || contestObj.end_at,
  );
  const contestWinnerAnnouncementDate = formatDate(
    contestObj.winner_announcement_at ||
      contestObj.winner_announcement_date ||
      contestObj.winner_announcement,
  );
  const contestStatus = contestObj.status || "";
  const contestPrize =
    contestObj.prize || contestObj.prize_pool || contestObj.reward || "";
  const contestPrizePool = contestObj.prize_pool || contestObj.prize || "";
  const contestWinnerCount =
    contestObj.winner_count !== undefined && contestObj.winner_count !== null
      ? String(contestObj.winner_count)
      : "";
  const contestParticipantsCount =
    contestObj.participants_count !== undefined &&
    contestObj.participants_count !== null
      ? String(contestObj.participants_count)
      : "";
  const contestVenueName =
    contestObj.venue_name || contestObj.venue?.name || "";
  const contestCity =
    contestObj.city || contestObj.venue?.city || "";
  const contestUrl = contestSlug
    ? `${SITE_CONFIG.platform_url || ""}/contest/${contestSlug}`
    : "";

  return {
    ...SITE_CONFIG,

    // Location
    // Stays a slug: CMS content builds internal links and JSON-LD URLs from it
    // ("blogs/...-in-{{current_city}}", "tykkit.com/{{current_city}}/music-shows").
    // Prose (titles, descriptions) swaps in city_name — see generateMetadata.
    current_city: (effectiveCity || city || "ahmedabad").toLowerCase().replace(/\s+/g, "-"),
    city_name: effectiveCity || "Ahmedabad",
    current_city_name: effectiveCity || "Ahmedabad",
    city: (effectiveCity || city || "ahmedabad").toLowerCase().replace(/\s+/g, "-"),
    city_slug: (effectiveCity || city || "ahmedabad").toLowerCase().replace(/\s+/g, "-"),
    current_state: state || venueObj?.state || "Gujarat",
    current_country: country || venueObj?.country || "India",
    current_pincode: pincode || venueObj?.pincode || "380001",

    // Date & Time
    current_date: now.format("DD MMM YYYY"),
    current_year: now.format("YYYY"),
    current_month: now.format("MMMM"),
    current_day: now.format("dddd"),
    current_time: now.format("hh:mm A"),

    // Blog
    blog_title: blogTitle,
    blog_name: blogTitle,
    blog_slug: blogSlug,
    blog_author: blogAuthor,
    author_name: blogAuthor,
    blog_date: blogDate,
    blog_published_date: blogDate,
    blog_reading_time: blogReadingTime,
    reading_time: blogReadingTime,
    blog_category: blogCategory,
    blog_tags: blogTags,
    blog_excerpt: blogExcerpt,
    blog_description: blogDescription,
    blog_url: blogUrl,

    // Artist
    artist_name: artistFullName,
    artist_first_name: artistFirstName,
    artist_last_name: artistLastName,
    artist_alternate_name: artistAlternateName,
    artist_stage_name: artistAlternateName,
    artist_slug: artistSlug,
    artist_genre: artistGenre,
    artist_genres: artistGenres,
    artist_type: artistType,
    artist_bio: artistBio,
    artist_description: artistBio,
    artist_tags: artistTags,
    artist_badges: artistBadges,
    artist_events_count: artistEventsCount,
    artist_past_events_count: artistEventsCount,
    artist_url: artistUrl,

    // Event
    event_name: resolvedEvent?.name || resolvedEvent?.title || "",
    event_slug: resolvedEvent?.slug || "",
    event_date: formatDate(resolvedEvent?.start_date),
    event_start_date: formatDate(resolvedEvent?.start_date),
    event_end_date: formatDate(resolvedEvent?.end_date),
    event_time: formatTime(resolvedEvent?.start_time),
    event_start_time: formatTime(resolvedEvent?.start_time),
    event_end_time: formatTime(resolvedEvent?.end_time),
    event_price:
      resolvedEvent?.price !== undefined && resolvedEvent?.price !== null
        ? String(resolvedEvent.price)
        : "",
    event_lowest_price:
      resolvedEvent?.lowest_price !== undefined &&
      resolvedEvent?.lowest_price !== null
        ? String(resolvedEvent.lowest_price)
        : resolvedEvent?.price !== undefined && resolvedEvent?.price !== null
          ? String(resolvedEvent.price)
          : "",
    currency: resolvedEvent?.currency || "INR",
    event_status: resolvedEvent?.status || "",
    event_description: toMetaDescription(resolvedEvent?.description),
    event_banner_image: extractFirstImageUrl(
      resolvedEvent?.image || resolvedEvent?.feature_images,
    ),
    event_min_age:
      resolvedEvent?.min_age !== undefined && resolvedEvent?.min_age !== null
        ? String(resolvedEvent.min_age)
        : "",
    event_max_age:
      resolvedEvent?.max_age !== undefined && resolvedEvent?.max_age !== null
        ? String(resolvedEvent.max_age)
        : "",
    event_url: resolvedEvent?.slug
      ? `${SITE_CONFIG.platform_url || ""}/event/${resolvedEvent.slug}`
      : "",

    // Venue
    venue_name: venueName,
    venue_address: venueAddress,
    venue_phone: venueObj?.phone || "",
    venue_website: venueObj?.website || "",
    venue_place_id: venueObj?.place_id || venueObj?.placeId || "",
    venue_url:
      venueObj?.place_id || venueObj?.placeId
        ? `${SITE_CONFIG.platform_url || ""}/venue/${venueObj.place_id || venueObj.placeId}`
        : "",
    event_city: effectiveCity,
    venue_city: effectiveCity,
    event_state: venueObj?.state || state || "",
    event_country: venueObj?.country || country || "",

    // Organizer
    organizer_name: organizerName,
    organizer_email: organizerEmail,
    organizer_phone: organizerPhone,
    organizer_website: organizerWebsite,
    organizer_slug: organizerSlug,
    organizer_workspace_slug: organizerWorkspaceSlug,
    organizer_url: organizerUrl,
    organizer_description: organizerDescription,
    organizer_bio: organizerDescription,
    organizer_public_events_count: organizerPublicEventsCount,
    organizer_events_count: organizerPublicEventsCount,
    organizer_city: organizerCity,
    organizer_state: organizerState,
    organizer_country: organizerCountry,
    organizer_pincode: organizerPincode,
    organizer_address: organizerAddress,
    organizer_logo: organizerLogo,
    organizer_image: organizerLogo,

    // Category
    category_name: categoryName,
    category_slug: categorySlug,
    category_description: categoryDescription,
    category_url: categoryUrl,

    // Contest
    contest_name: contestName,
    contest_slug: contestSlug,
    contest_start_date: contestStartDate,
    contest_end_date: contestEndDate,
    contest_winner_announcement_date: contestWinnerAnnouncementDate,
    contest_status: contestStatus,
    contest_prize: contestPrize,
    contest_prize_pool: contestPrizePool,
    contest_winner_count: contestWinnerCount,
    contest_participants_count: contestParticipantsCount,
    contest_venue_name: contestVenueName,
    contest_city: contestCity,
    contest_url: contestUrl,

    // Activity
    event_type: resolvedEvent?.activity_types?.join(", ") || categoryName || "",
    event_distance: resolvedEvent?.distances?.join(", ") || "",

    // Customer
    customer_name: user?.name || user?.fullName || "",
    customer_email: user?.email || "",
    customer_phone: user?.phone || user?.mobile || "",

    // Booking & Ticket
    booking_id: booking?.registration_id || booking?.id || "",
    ticket_count:
      booking?.ticket_count !== undefined && booking?.ticket_count !== null
        ? String(booking.ticket_count)
        : booking?.no_of_tickets !== undefined &&
            booking?.no_of_tickets !== null
          ? String(booking.no_of_tickets)
          : "",
    convenience_fee:
      booking?.convenience_fee !== undefined &&
      booking?.convenience_fee !== null
        ? String(booking.convenience_fee)
        : "",
    refund_days:
      booking?.refund_days !== undefined && booking?.refund_days !== null
        ? String(booking.refund_days)
        : "",
    cancellation_hours:
      booking?.cancellation_hours !== undefined &&
      booking?.cancellation_hours !== null
        ? String(booking.cancellation_hours)
        : "",

    // Short aliases used in CMS templates
    slug:
      blogSlug ||
      artistSlug ||
      resolvedEvent?.slug ||
      categorySlug ||
      contestSlug ||
      "",
    title:
      blogTitle ||
      artistFullName ||
      resolvedEvent?.name ||
      categoryName ||
      contestName ||
      "",
    distance: resolvedEvent?.distances?.join(", ") || "",

    // Custom overrides
    ...(custom || {}),
  };
};

const TOKEN_PATTERN = /{{\s*([^}]+)\s*}}/g;

const replaceInString = (str, context) =>
  str.replace(TOKEN_PATTERN, (match, key) => {
    const trimmedKey = key.trim();
    const value = context[trimmedKey];
    return value === undefined || value === null ? "" : String(value);
  });

// Deep-walks strings/arrays/objects and resolves `{{token}}` occurrences
// anywhere inside, so a single call at the top of a CMS payload (e.g. a
// section's `data`) resolves every nested title/description/HTML field.
export const resolveDynamicVariables = (value, context) => {
  if (!context || typeof context !== "object") return value;
  if (typeof value === "string") return replaceInString(value, context);
  if (Array.isArray(value)) {
    return value.map((item) => resolveDynamicVariables(item, context));
  }
  if (value && typeof value === "object") {
    const result = {};
    for (const key of Object.keys(value)) {
      result[key] = resolveDynamicVariables(value[key], context);
    }
    return result;
  }
  return value;
};

// Convenience helpers
export const replaceDynamicEventValues = (content = "", eventData = {}) => {
  if (!content) return content;
  return resolveDynamicVariables(
    content,
    buildDynamicContext({ event: eventData }),
  );
};

export const replaceDynamicBlogValues = (content = "", blogData = {}) => {
  if (!content) return content;
  return resolveDynamicVariables(
    content,
    buildDynamicContext({ blog: blogData }),
  );
};

export const replaceDynamicArtistValues = (content = "", artistData = {}) => {
  if (!content) return content;
  return resolveDynamicVariables(
    content,
    buildDynamicContext({ artist: artistData }),
  );
};

export const replaceDynamicContestValues = (content = "", contestData = {}) => {
  if (!content) return content;
  return resolveDynamicVariables(
    content,
    buildDynamicContext({ contest: contestData }),
  );
};

export const replaceDynamicCategoryValues = (
  content = "",
  categoryData = {},
) => {
  if (!content) return content;
  return resolveDynamicVariables(
    content,
    buildDynamicContext({ category: categoryData }),
  );
};

export const replaceDynamicOrganizerValues = (
  content = "",
  organizerData = {},
) => {
  if (!content) return content;
  return resolveDynamicVariables(
    content,
    buildDynamicContext({ organizer: organizerData }),
  );
};
